import type { HandleChangeAnswerType } from '~/components/Interaction/Form/Question/model';
import type { AnswerUserType } from '~/model/AnswerUserType';
import type { QuestionChoiceType } from '~/model/QuestionChoiceType';
import classNames from 'classnames';
import { useEffect, useLayoutEffect, useState } from 'react';
import { CheckboxGroup } from '~/components/commons/CheckboxGroup/CheckboxGroup';
import { useFocus } from '~/hooks/useFocus';
import { ellipsis } from '~/utils/utils';
import { checkIfHasPictureChoice } from '../../utils';
import { ChoicePicture } from './ChoicePicture';

export const CheckTypeQuestion = ({
  answerUser,
  answerChoices,
  questionId,
  handleChangeAnswer,
  type,
  openGallery,
  langValue,
  disabled = false,
}: ChoiceTypeQuestionPropsType) => {
  const [inputRef, setInputFocus] = useFocus();
  const [hasFocused, setHasFocused] = useState(false);
  const hasPictureChoice = checkIfHasPictureChoice(answerChoices);

  const value = answerUser[questionId]?.answer_choices
    ? (answerUser[questionId].answer_choices as string[])
    : [];

  useEffect(() => setHasFocused(false), [questionId]);

  useLayoutEffect(() => {
    if (!hasFocused && !disabled && !!inputRef.current) {
      setInputFocus();
      setHasFocused(true);
    }
  }, [!!inputRef.current, disabled]);

  return (
    answerChoices && (
      <CheckboxGroup
        name="check-type-question"
        key={questionId}
        value={value}
        onChange={values =>
          handleChangeAnswer(values, questionId, type, undefined, false)}
      >
        {Checkbox => (
          <>
            {answerChoices.map((choice: QuestionChoiceType, index: number) => {
              const choiceUri = choice['@id'];
              const choiceId = choice.id;
              const isChecked = value.includes(choiceUri);
              const shouldHaveRef = index === 0;
              const label = hasPictureChoice
                ? ellipsis(choice.title, 50)
                : choice.title;

              return (
                <div
                  className="form-group checkbox"
                  data-testid="checkbox"
                  key={`${questionId}-${choiceId}`}
                >
                  <div
                    className={classNames('control-checkbox', {
                      'control-checkbox--selected': isChecked,
                      form__choice: !hasPictureChoice,
                    })}
                  >
                    {hasPictureChoice && (
                      <ChoicePicture
                        choice={choice}
                        openGallery={openGallery}
                        isChecked={isChecked}
                      />
                    )}
                    <span
                      className={classNames('control-legend', {
                        'control-legend--selected': isChecked,
                      })}
                    >
                      <Checkbox
                        value={choiceUri}
                        disabled={disabled}
                        id={`choice-${choiceId}`}
                        className={classNames('control-input', {
                          'vtmn-checkbox': !hasPictureChoice,
                        })}
                        data-cy="check"
                      />
                      <label
                        ref={shouldHaveRef ? inputRef : null}
                        className="form__label"
                        htmlFor={`choice-${choiceId}`}
                        title={choice.title}
                      >
                        <span lang={langValue}>{label}</span>
                      </label>
                    </span>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </CheckboxGroup>
    )
  );
};

export interface ChoiceTypeQuestionPropsType {
  answerUser: AnswerUserType;
  answerChoices: QuestionChoiceType[];
  questionId: string;
  handleChangeAnswer: HandleChangeAnswerType;
  type: string;
  openGallery: (url: string) => void;
  langValue: string;
  disabled?: boolean;
}
